import { CircularProgress, Typography, useTheme } from '@mui/material';
import { athleteDisplayName, AthletePreview } from '@strength/domain';
import {
  groupEntriesByWorkout,
  WorkoutExercise,
} from '@strength/domain-workout';
import { formatDate, sortByOrder } from '@strength/utils';
import { MdHistory } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import { RouterOutput } from '../../../../../../trpc/common';
import { trpc } from '../../../../../../trpc/react';
import { WorkoutStatusBadge } from '../../../../../components/badge/workout-status-badge';
import { Drawer, DrawerHeader } from '../../../../../components/drawer/drawer';
import { DataTablePlaceholder } from '../../../../../components/table/data-table-placeholder/data-table-placeholder';
import { useMetrics } from '../../../../../hooks/useMetrics';
import { getLocale } from '../../../../../utils/i18n';
import { getTodayDateString } from '../../group-workout-utils';

type ExerciseHistoryData =
  RouterOutput['actuals']['exerciseActuals']['getByLibraryExerciseIds'][string][0];
type ExerciseHistorySet = ExerciseHistoryData['sets'][0];
type ExerciseHistoryMetric = ExerciseHistorySet['metrics'][0];

type Metric = {
  metricId: string;
  name: string;
};

type ExerciseHistoryDrawerProps = {
  athlete: AthletePreview;
  exercise: WorkoutExercise | undefined;
  open: boolean;
  onClose: () => void;
};

export const ExerciseHistoryDrawer = ({
  athlete,
  exercise,
  open,
  onClose,
}: ExerciseHistoryDrawerProps) => {
  const {
    palette: { mediumEmphasisTextLight },
  } = useTheme();
  const { t } = useTranslation();

  const { data: exerciseHistoryData, isLoading } =
    trpc.actuals.exerciseActuals.getByLibraryExerciseIds.useQuery(
      {
        profileId: athlete.id,
        libraryExerciseId: exercise ? exercise.libraryExerciseId : '',
        limit: 5,
        endDate: getTodayDateString(),
      },
      {
        enabled: !!exercise && open,
      },
    );

  if (!exercise) return null;
  const { exerciseName } = exercise;

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="h3">
          {t('exerciseHistoryTitle', { exerciseName })}
        </Typography>
        <Typography variant="body2" color={mediumEmphasisTextLight}>
          {athleteDisplayName(athlete)}
        </Typography>
      </DrawerHeader>

      {isLoading ? (
        <LoadingState />
      ) : (
        <>
          {exerciseHistoryData &&
          Object.keys(exerciseHistoryData).length > 0 ? (
            <div className="px-6 overflow-y-auto">
              {Object.entries(exerciseHistoryData).map(
                ([completedDate, entries]) => (
                  <DateGroup key={completedDate} date={completedDate}>
                    {groupEntriesByWorkout(entries).map((group) => (
                      <WorkoutGroup
                        key={group.workoutId}
                        workoutName={group.workoutName}
                      >
                        {group.entries.map((entry) => (
                          <ExerciseEntry key={entry.id} entry={entry} />
                        ))}
                      </WorkoutGroup>
                    ))}
                  </DateGroup>
                ),
              )}
            </div>
          ) : (
            <DataTablePlaceholder
              icon={<MdHistory size={20} className="text-white" />}
              title={t('noAvailableHistory')}
              description={t('noExerciseHistoryDescription')}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

const LoadingState = () => {
  const {
    palette: { mediumEmphasisTextLight },
  } = useTheme();

  return (
    <div className="p-4 flex justify-center items-center h-32">
      <Typography variant="body2" color={mediumEmphasisTextLight}>
        <CircularProgress size={20} />
      </Typography>
    </div>
  );
};

const DateGroup = ({
  date,
  children,
}: {
  date: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="mb-6">
      <Typography variant="h4" className="mb-1 text-gray-700 font-semibold">
        {formatDate(date, getLocale(), {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </Typography>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
};

const SkippedExerciseEntry = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <div className="p-4 rounded-xl bg-gray-50">
        <Typography>{t('thisExerciseWasSkipped')}</Typography>
      </div>
    </div>
  );
};

const WorkoutGroup = ({
  workoutName,
  children,
}: {
  workoutName: string;
  children: React.ReactNode;
}) => {
  const {
    palette: { mediumEmphasisTextLight },
  } = useTheme();

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="subtitle1" color={mediumEmphasisTextLight}>
        {workoutName}
      </Typography>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
};

const MetricHeaders = ({ metrics }: { metrics: Metric[] }) => {
  const {
    palette: { highEmphasisTextLight },
  } = useTheme();

  return (
    <>
      {metrics.map((metric) => (
        <div key={metric.metricId} className="text-center">
          <Typography variant="subtitle1Semibold" color={highEmphasisTextLight}>
            {metric.name}
          </Typography>
        </div>
      ))}
    </>
  );
};

const SetRow = ({
  set,
  setIndex,
  metrics,
}: {
  set: ExerciseHistorySet;
  setIndex: number;
  metrics: Metric[];
}) => {
  const {
    palette: { highEmphasisTextLight },
  } = useTheme();

  return (
    <>
      {metrics.map((metric, metricIndex) => {
        let metricValue = '';

        if (metric.metricId === 'set') {
          metricValue = (setIndex + 1).toString();
        } else if (set.status === 'skipped') {
          metricValue = '';
        } else {
          metricValue =
            set.metrics?.find(
              (m: ExerciseHistoryMetric) => m.metricId === metric.metricId,
            )?.value ?? '';
        }

        return (
          <div
            key={`${set.workoutSetId}-${metric.metricId}`}
            className="text-center"
          >
            {set.status === 'skipped' && metricIndex === 1 ? (
              <WorkoutStatusBadge status={{ type: 'skipped' }} />
            ) : (
              <Typography variant="body1" color={highEmphasisTextLight}>
                {metricValue}
              </Typography>
            )}
          </div>
        );
      })}
    </>
  );
};

const ExerciseEntry = ({ entry }: { entry: ExerciseHistoryData }) => {
  const { t } = useTranslation();
  const { getMetric } = useMetrics();

  if (entry.status === 'skipped') {
    return <SkippedExerciseEntry />;
  }

  const metricsInExercise: Metric[] = [
    {
      metricId: 'set',
      name: t('set'),
    },
    ...sortByOrder(entry.sets[0]?.metrics || []).map(
      (metric: ExerciseHistoryMetric) => {
        const metricData = getMetric(metric.metricId);
        return {
          metricId: metricData.id,
          name: metricData.name,
        };
      },
    ),
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="p-4 rounded-xl bg-gray-50 overflow-x-auto">
        <div
          className="grid gap-4 "
          style={{
            gridTemplateColumns: `repeat(${metricsInExercise.length}, minmax(50px, 1fr))`,
            minWidth: 'max-content',
          }}
        >
          <MetricHeaders metrics={metricsInExercise} />

          {sortByOrder([...entry.sets]).map(
            (set: ExerciseHistorySet, setIndex: number) => (
              <SetRow
                key={set.workoutSetId}
                set={set}
                setIndex={setIndex}
                metrics={metricsInExercise}
              />
            ),
          )}
        </div>
      </div>
    </div>
  );
};
